import { useState, useEffect } from 'react';
import { API_BASE, API_BASE_URL } from '@/utils/api';

// Default branding for NEXEDU platform
const DEFAULT_BRANDING: TenantBranding = {
  found: false,
  name: 'NEXEDU',
  logoUrl: null,
  primaryColor: '#1E40AF',
  secondaryColor: '#6366F1',
  description: 'Plateforme de gestion pour l\'enseignement superieur',
  isDevelopment: true,
};

export interface TenantBranding {
  found: boolean;
  tenantId?: string;
  name: string;
  subdomain?: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor?: string;
  description?: string;
  loginUrl?: string;
  isDevelopment?: boolean;
  isReserved?: boolean;
}

interface UseTenantBrandingResult {
  branding: TenantBranding;
  isLoading: boolean;
  error: string | null;
  isInstitutionPage: boolean;
}

/**
 * Hook to fetch tenant branding based on current hostname
 * Used for dynamic login page branding in white-label multi-tenancy
 */
export function useTenantBranding(): UseTenantBrandingResult {
  const [branding, setBranding] = useState<TenantBranding>(DEFAULT_BRANDING);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBranding = async () => {
      const hostname = window.location.hostname;
      const searchParams = new URLSearchParams(window.location.search);
      const tenantParam = searchParams.get('tenant');

      // Check for tenant query parameter (useful for development testing)
      if (tenantParam && (hostname === 'localhost' || hostname.startsWith('127.0.0.1'))) {
        try {
          const response = await fetch(
            `${API_BASE}/tenants/public/branding-by-id?tenantId=${encodeURIComponent(tenantParam)}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            if (data.found) {
              const fullLogoUrl = data.logoUrl
                ? (data.logoUrl.startsWith('http') ? data.logoUrl : `${API_BASE_URL}${data.logoUrl}`)
                : null;

              setBranding({
                found: true,
                tenantId: data.tenantId,
                name: data.name,
                subdomain: data.subdomain,
                logoUrl: fullLogoUrl,
                primaryColor: data.primaryColor || '#1E40AF',
                secondaryColor: data.secondaryColor || '#6366F1',
                description: data.description,
                loginUrl: data.loginUrl,
                isDevelopment: true,
              });
              setIsLoading(false);
              return;
            }
          }
        } catch (err) {
          console.error('Error fetching tenant by ID:', err);
        }
        // Fall through to default branding if tenant not found
      }

      // Skip for localhost development without tenant param - use default NEXEDU branding
      if (hostname === 'localhost' || hostname.startsWith('127.0.0.1')) {
        setBranding(DEFAULT_BRANDING);
        setIsLoading(false);
        return;
      }

      const baseDomain = import.meta.env.VITE_BASE_DOMAIN || 'nexedu.ma';

      // Apex marketing domain and www host the platform itself, not a tenant.
      // Without this guard, /login on nexedu.ma resolves "nexedu" as a tenant
      // slug and renders "Institution non trouvee" instead of the platform login.
      if (hostname === baseDomain || hostname === `www.${baseDomain}`) {
        setBranding(DEFAULT_BRANDING);
        setIsLoading(false);
        return;
      }

      // Check if this is the admin subdomain
      if (hostname === `admin.${baseDomain}`) {
        setBranding({
          ...DEFAULT_BRANDING,
          found: false,
          isReserved: true,
          isDevelopment: false,
        });
        setIsLoading(false);
        return;
      }

      // Fetch with retries: a single failed request (server restart, network
      // blip) must never be mistaken for "institution not found".
      const fetchWithRetry = async (): Promise<Response> => {
        let lastError: unknown;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const response = await fetch(
              `${API_BASE}/tenants/public/branding?hostname=${encodeURIComponent(hostname)}`,
              {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
              }
            );
            if (response.ok) return response;
            lastError = new Error(`HTTP ${response.status}`);
          } catch (err) {
            lastError = err;
          }
          await new Promise((r) => setTimeout(r, 700 * (attempt + 1)));
        }
        throw lastError;
      };

      const cacheKey = `nexedu_branding_${hostname}`;

      try {
        const response = await fetchWithRetry();

        const data = await response.json();

        if (data.found) {
          // Prepend API_BASE_URL to logoUrl if it's a relative path
          const fullLogoUrl = data.logoUrl
            ? (data.logoUrl.startsWith('http') ? data.logoUrl : `${API_BASE_URL}${data.logoUrl}`)
            : null;

          const resolved: TenantBranding = {
            found: true,
            tenantId: data.tenantId,
            name: data.name,
            subdomain: data.subdomain,
            logoUrl: fullLogoUrl,
            primaryColor: data.primaryColor || '#1E40AF',
            secondaryColor: data.secondaryColor || '#6366F1',
            description: data.description,
            loginUrl: data.loginUrl,
          };
          setBranding(resolved);
          // Remember last-known-good branding so a later network blip
          // (e.g. right after logout) still renders the tenant login page
          try {
            localStorage.setItem(cacheKey, JSON.stringify(resolved));
          } catch {
            // Storage full/blocked: caching is best-effort only
          }
        } else if (data.isDevelopment) {
          setBranding(DEFAULT_BRANDING);
        } else if (data.isReserved) {
          setBranding({
            ...DEFAULT_BRANDING,
            found: false,
            isReserved: true,
            isDevelopment: false,
          });
        } else {
          // Tenant not found
          setError('Institution non trouvee');
          setBranding({
            ...DEFAULT_BRANDING,
            found: false,
            isDevelopment: false,
          });
        }
      } catch (err) {
        console.error('Error fetching tenant branding:', err);
        // Network failure after retries: fall back to the cached branding for
        // this hostname so the login page still works. NEVER show
        // "institution not found" for a connectivity problem.
        let cached: TenantBranding | null = null;
        try {
          const raw = localStorage.getItem(cacheKey);
          if (raw) cached = JSON.parse(raw) as TenantBranding;
        } catch {
          cached = null;
        }
        if (cached?.found) {
          setBranding(cached);
        } else {
          setError('Erreur de chargement');
          setBranding({
            ...DEFAULT_BRANDING,
            found: false,
            isDevelopment: false,
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchBranding();
  }, []);

  // Determine if this is an institution-specific page (not localhost, not admin)
  const isInstitutionPage = branding.found && !branding.isDevelopment && !branding.isReserved;

  return { branding, isLoading, error, isInstitutionPage };
}

/**
 * Get the logout redirect URL based on user info
 * Returns the tenant-specific login URL or default login path
 */
export function getLogoutRedirectUrl(user: {
  role: string;
  tenantLoginUrl?: string;
  tenantSubdomain?: string;
} | null): string {
  if (!user) return '/login';

  // Super admin logs in from the apex domain (no dedicated subdomain in V1).
  if (user.role === 'SUPER_ADMIN') {
    return '/login';
  }

  // If user has a tenant login URL, use it
  if (user.tenantLoginUrl) {
    // In development, stay on localhost
    if (window.location.hostname === 'localhost') {
      return '/login';
    }
    return user.tenantLoginUrl;
  }

  // Fallback to regular login
  return '/login';
}

